'use client';

import { c as _c } from "react/compiler-runtime";
import { faCircle } from '@fortawesome/free-regular-svg-icons';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@uzh-bf/design-system';
import { memo, useEffect, useState } from 'react';
import { useSWRConfig } from 'swr';
import useSWRMutation from 'swr/mutation';
import { twMerge } from 'tailwind-merge';
import { useCachedUnitProgressSWR } from '~/app/actions/progress';
import { markBlockAsCompleted } from '~/app/actions/unitProgress.server';
import { CACHE_KEYS } from '~/lib/cache';
import { useAuthToken } from '~/lib/hooks';
import { trackEvent } from '~/lib/matomo';
const CompletionTag = memo(function CompletionTag(t0) {
  const $ = _c(46);
  const {
    courseId,
    moduleId,
    unitId,
    blockIdentifier,
    blockTime,
    className,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "button" : t1;
  const jwt = useAuthToken();
  const {
    mutate: globalMutate
  } = useSWRConfig();
  let t2;
  if ($[0] !== courseId || $[1] !== moduleId || $[2] !== unitId) {
    t2 = {
      courseId,
      moduleId,
      unitId
    };
    $[0] = courseId;
    $[1] = moduleId;
    $[2] = unitId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data,
    mutate: mutateCache
  } = useCachedUnitProgressSWR(t2);
  let t3;
  if ($[4] !== blockIdentifier || $[5] !== data?.completedBlocks) {
    t3 = data?.completedBlocks?.some(o => o.blockIdentifier === String(blockIdentifier));
    $[4] = blockIdentifier;
    $[5] = data?.completedBlocks;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const serverIsCompleted = t3;
  const [localIsCompleted, setLocalIsCompleted] = useState(serverIsCompleted ?? false);
  let t4;
  let t5;
  if ($[7] !== serverIsCompleted) {
    t4 = () => {
      if (serverIsCompleted !== undefined) {
        setLocalIsCompleted(serverIsCompleted);
      }
    };
    t5 = [serverIsCompleted];
    $[7] = serverIsCompleted;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {};
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const {
    trigger
  } = useSWRMutation("markBlockAsCompleted", markBlockAsCompleted, t6);
  let t7;
  if ($[11] !== blockIdentifier || $[12] !== blockTime || $[13] !== courseId || $[14] !== globalMutate || $[15] !== moduleId || $[16] !== mutateCache || $[17] !== unitId) {
    t7 = newValue => {
      const normalizedBlockIdent = String(blockIdentifier);
      let timeDelta = 0;
      let unitCompletionDelta = 0;
      mutateCache(current => {
        if (!current) {
          return current;
        }
        const completedBlocks = current.completedBlocks || [];
        const onceCompletedBlocks = current.onceCompletedBlocks || [];
        const wasBlockCompleted = completedBlocks.some(b => b.blockIdentifier === normalizedBlockIdent);
        const wasBlockOnceCompleted = onceCompletedBlocks.some(b_0 => b_0.blockIdentifier === normalizedBlockIdent);
        let newCompleted = completedBlocks;
        let newOnceCompleted = onceCompletedBlocks;
        if (newValue && !wasBlockCompleted) {
          newCompleted = [...completedBlocks, {
            blockIdentifier: normalizedBlockIdent
          }];
          timeDelta = blockTime ?? 0;
        } else {
          if (!newValue && wasBlockCompleted) {
            newCompleted = completedBlocks.filter(b_1 => b_1.blockIdentifier !== normalizedBlockIdent);
            timeDelta = -(blockTime ?? 0);
          }
        }
        if (newValue && !wasBlockOnceCompleted) {
          newOnceCompleted = [...onceCompletedBlocks, {
            blockIdentifier: normalizedBlockIdent
          }];
        }
        const totalBlocks = current.totalBlocks ?? 0;
        const wasUnitComplete = completedBlocks.length >= totalBlocks && totalBlocks > 0;
        const willBeUnitComplete = newCompleted.length >= totalBlocks && totalBlocks > 0;
        unitCompletionDelta = wasUnitComplete === willBeUnitComplete ? 0 : willBeUnitComplete ? 1 : -1;
        const currentTime = current.time || 0;
        const newTime = Math.max(0, currentTime + timeDelta);
        const progressPercentage = totalBlocks > 0 ? newCompleted.length / totalBlocks * 100 : 0;
        return {
          ...current,
          completedBlocks: newCompleted,
          onceCompletedBlocks: newOnceCompleted,
          time: newTime,
          progressPercentage
        };
      }, false);
      if (timeDelta === 0 && unitCompletionDelta === 0) {
        return;
      }
      const courseProgressKey = CACHE_KEYS.COURSE_UNIT_TIME_PROGRESS({
        courseId
      });
      const numericUnitId = Number(unitId);
      const numericModuleId = Number(moduleId);
      globalMutate(courseProgressKey, current_0 => {
        if (!current_0) {
          return current_0;
        }
        const completedMinutes = Math.min(current_0.totalMinutes, Math.max(0, current_0.completedMinutes + timeDelta));
        const completedUnits = Math.min(current_0.totalUnits, Math.max(0, current_0.completedUnits + unitCompletionDelta));
        const progressPercentage_0 = current_0.totalMinutes > 0 ? Math.round(completedMinutes / current_0.totalMinutes * 100) : 0;
        const unitCompletedMinutes = {
          ...(current_0.unitCompletedMinutes ?? {}),
          [numericUnitId]: Math.max(0, (current_0.unitCompletedMinutes?.[numericUnitId] ?? 0) + timeDelta)
        };
        const moduleCompletedMinutes = {
          ...(current_0.moduleCompletedMinutes ?? {}),
          [numericModuleId]: Math.max(0, (current_0.moduleCompletedMinutes?.[numericModuleId] ?? 0) + timeDelta)
        };
        return {
          ...current_0,
          completedUnits,
          completedMinutes,
          progressPercentage: progressPercentage_0,
          unitCompletedMinutes,
          moduleCompletedMinutes
        };
      }, false);
    };
    $[11] = blockIdentifier;
    $[12] = blockTime;
    $[13] = courseId;
    $[14] = globalMutate;
    $[15] = moduleId;
    $[16] = mutateCache;
    $[17] = unitId;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const updateAllInstances = t7;
  let t8;
  if ($[19] !== blockIdentifier || $[20] !== courseId || $[21] !== jwt || $[22] !== localIsCompleted || $[23] !== moduleId || $[24] !== trigger || $[25] !== unitId || $[26] !== updateAllInstances) {
    t8 = e => {
      e?.preventDefault();
      e?.stopPropagation();
      if (!jwt) {
        console.warn("[CompletionTag] No JWT, skipping");
        return;
      }
      const clickTimestamp = performance.now();
      const newValue_0 = !localIsCompleted;
      trackEvent({
        category: "progress",
        action: "block_complete_toggle",
        name: newValue_0 ? "on" : "off"
      });
      setLocalIsCompleted(newValue_0);
      updateAllInstances(newValue_0);
      trigger({
        newValue: newValue_0,
        unitId: Number(unitId),
        moduleId: Number(moduleId),
        courseId: Number(courseId),
        blockIdentifier,
        jwt
      }).then(() => {
        const totalTime = performance.now() - clickTimestamp;
        console.warn(`[CompletionTag] Server mutation completed in ${totalTime.toFixed(2)}ms for block ${blockIdentifier}`);
      }).catch(error => {
        const totalTime_0 = performance.now() - clickTimestamp;
        console.error(`[CompletionTag] Server mutation failed after ${totalTime_0.toFixed(2)}ms for block ${blockIdentifier}:`, error);
        setLocalIsCompleted(!newValue_0);
        updateAllInstances(!newValue_0);
      });
    };
    $[19] = blockIdentifier;
    $[20] = courseId;
    $[21] = jwt;
    $[22] = localIsCompleted;
    $[23] = moduleId;
    $[24] = trigger;
    $[25] = unitId;
    $[26] = updateAllInstances;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const handleClick = t8;
  const t9 = localIsCompleted ? faCircleCheck : faCircle;
  let t10;
  if ($[28] !== t9) {
    t10 = <FontAwesomeIcon icon={t9} className="h-4 w-4" />;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const icon = t10;
  if (variant === "icon") {
    const t11 = localIsCompleted ? "text-uzh-lightgreen" : "text-gray-500";
    let t12;
    if ($[30] !== className || $[31] !== t11) {
      t12 = twMerge("flex-shrink-0", t11, className);
      $[30] = className;
      $[31] = t11;
      $[32] = t12;
    } else {
      t12 = $[32];
    }
    let t13;
    if ($[33] !== handleClick || $[34] !== icon || $[35] !== t12) {
      t13 = <button type="button" className={t12} onClick={handleClick}>{icon}</button>;
      $[33] = handleClick;
      $[34] = icon;
      $[35] = t12;
      $[36] = t13;
    } else {
      t13 = $[36];
    }
    return t13;
  }
  const t11 = localIsCompleted ? "text-uzh-lightgreen" : "text-gray-500";
  let t12;
  if ($[37] !== className || $[38] !== t11) {
    t12 = twMerge("rounded-full w-8 h-8 bg-white flex-shrink-0", t11, className);
    $[37] = className;
    $[38] = t11;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== t12) {
    t13 = {
      root: t12
    };
    $[40] = t12;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== handleClick || $[43] !== icon || $[44] !== t13) {
    t14 = <Button className={t13} onClick={handleClick}>{icon}</Button>;
    $[42] = handleClick;
    $[43] = icon;
    $[44] = t13;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  return t14;
});
export default CompletionTag;